import { useState } from 'react';
import type { ApplyChannel } from './useApplyChannel';

const METHOD_LABEL: Record<string, string> = {
  email: '이메일 접수',
  post: '우편 접수',
  visit: '방문 제출',
  phone: '전화 문의',
  homepage: '홈페이지 접수',
  site: '사역자모아 지원',
};

const isEmail = (v: unknown): v is string => typeof v === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
const phoneOf = (v: unknown) => (typeof v === 'string' && v.replace(/\D/g, '').length >= 9 && v.replace(/\D/g, '').startsWith('0') ? v.trim() : null);

interface RecruitApplyPanelProps {
  title: string;
  shareUrl: string;
  /** 사이트 지원 대상 게시판인지 (국내·해외 사역지) */
  siteApplyBoard: boolean;
  closed: boolean;
  channel: ApplyChannel | null;
  /** 채널 API 가 없는 게시판(찬양대·기관)용 — 공고의 담당자 정보 */
  inquiry: { email?: string; phone?: string };
  originalLink?: string;
  onApply: () => void;
}

/**
 * 공고 상세 상단의 지원 경로 — 2트랙 (application-structure-final.md 2장)
 * 트랙 A 사이트 지원: 사이트가 접수·기록한다 (교회에는 첨부 없는 새 지원자 알림만 간다).
 * 트랙 B 이메일 주소: 사역자가 자기 메일로 직접 보낸다 — 사이트에 기록이 남지 않는다.
 * 두 트랙 모두 "교회가 읽었는지"는 사이트가 알 수 없으므로 도달·열람을 약속하지 않는다 (3장).
 */
export default function RecruitApplyPanel({ title, shareUrl, siteApplyBoard, closed, channel, inquiry, originalLink, onApply }: RecruitApplyPanelProps) {
  const email = channel ? channel.email : isEmail(inquiry.email) ? inquiry.email.trim() : null;
  const phone = channel ? channel.phone : phoneOf(inquiry.phone);
  const isClosed = closed || channel?.state === 'closed';
  const siteApply = siteApplyBoard && !isClosed && Boolean(channel && (channel.fileApply || channel.resumeApply));
  const methods = (channel?.methods || []).filter((m) => METHOD_LABEL[m]);
  // 가져온 공고 (aggregated-posting-lock-design.md 4장) — 예약 지원은 반드시 사역자에게 명시한다
  const reserve = channel?.applyMode === 'RESERVE';
  const external = channel?.applyMode === 'EXTERNAL';
  const sourceUrl = channel?.sourceUrl || originalLink;
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('이메일 주소를 복사해 주세요.', email);
    }
  };

  if (isClosed) {
    return (
      <div className="recruit-apply-panel recruit-apply-panel--closed">
        <div className="recruit-apply-panel__info">
          <div className="recruit-apply-panel__title">마감된 공고입니다</div>
          <div className="recruit-apply-panel__desc">비슷한 지역·직분의 새 공고를 알림으로 받아 보세요.</div>
        </div>
      </div>
    );
  }

  const mailto = email
    ? `mailto:${email}?subject=${encodeURIComponent(`[지원] ${title}`)}&body=${encodeURIComponent(`안녕하십니까.\n사역자모아에 올라온 공고를 보고 지원합니다.\n\n공고: ${shareUrl}\n\n`)}`
    : null;

  const desc = reserve && siteApply
    ? '이 공고는 교회가 아직 사역자모아에서 확인하지 않았습니다. 관심 표시는 교회가 확인하면 전달되며, 30일 내 확인되지 않으면 원문으로 직접 지원하시도록 안내해 드립니다.'
    : external
      ? '이 공고는 교회가 아직 사역자모아에서 확인하지 않아 사이트로 지원을 받을 수 없습니다. 원문 게시글의 안내대로 직접 지원해 주세요.'
      : siteApply
        ? channel?.fileApply
          ? '사역자모아로 지원하면 지원 내역이 기록되어 어디에 냈는지 한곳에서 볼 수 있고, 같은 공고에 두 번 내지 않도록 막아 드립니다. 가지고 있는 이력서 파일로, 회원가입 없이도 됩니다.'
          : '사역자모아로 지원하면 지원 내역이 기록되고, 저장한 이력서를 다음 지원에 그대로 다시 쓸 수 있습니다.'
        : email
          ? '교회 담당자 이메일로 직접 지원할 수 있습니다.'
          : '교회 이메일이 공개되지 않은 공고입니다. 전화나 원본 게시글의 안내로 문의해 주세요.';
  // 트랙 B — 사이트 지원과 함께 보일 때는 따로 묶어 "기록이 남지 않는다"를 밝힌다
  const directBox = Boolean(email && (siteApply || (external && sourceUrl)));

  return (
    <div className="recruit-apply-panel" id="apply">
      <div className="recruit-apply-panel__info">
        <div className="recruit-apply-panel__title">
          지원 방법
          {channel?.churchVerified && <span className="recruit-apply-panel__verified">인증 교회</span>}
        </div>
        <div className="recruit-apply-panel__desc">{desc}</div>
        {(methods.length > 0 || (email && !directBox) || phone) && (
          <div className="recruit-apply-panel__chips">
            {methods.map((m) => <span key={m}>{METHOD_LABEL[m]}</span>)}
            {email && !directBox && <span>{email}</span>}
            {phone && <span>{phone}</span>}
          </div>
        )}
      </div>
      <div className="recruit-apply-panel__actions">
        {siteApply && (
          <button type="button" className="recruit-apply-panel__btn recruit-apply-panel__btn--primary" onClick={onApply}>
            {reserve ? '관심 표시하기' : '사역자모아로 지원하기'}
          </button>
        )}
        {external && sourceUrl && (
          <a className="recruit-apply-panel__btn recruit-apply-panel__btn--primary" href={sourceUrl} target="_blank" rel="noopener noreferrer">
            원문에서 지원하기
          </a>
        )}
        {mailto && !directBox && (
          <a className="recruit-apply-panel__btn recruit-apply-panel__btn--primary" href={mailto}>
            내 메일로 직접 보내기
          </a>
        )}
        {phone && (
          <a className="recruit-apply-panel__btn" href={`tel:${phone.replace(/[^\d+]/g, '')}`}>
            전화 문의
          </a>
        )}
        {!siteApply && !mailto && !external && originalLink && (
          <a className="recruit-apply-panel__btn recruit-apply-panel__btn--primary" href={originalLink} target="_blank" rel="noopener noreferrer">
            원본 게시글에서 확인
          </a>
        )}
      </div>
      {directBox && mailto && (
        <div className="recruit-apply-panel__direct">
          <div className="recruit-apply-panel__direct-info">
            <strong>내 메일로 직접 보내기</strong>
            <span className="recruit-apply-panel__direct-email">{email}</span>
            <span>평소 쓰는 메일로 교회에 직접 보낼 수 있습니다. 이력서 파일은 메일앱에서 직접 첨부해 주세요. 직접 보낸 메일은 사역자모아에 지원 기록이 남지 않습니다.</span>
          </div>
          <div className="recruit-apply-panel__actions">
            <button type="button" className="recruit-apply-panel__btn" onClick={copyEmail}>
              {copied ? '복사됨' : '주소 복사'}
            </button>
            <a className="recruit-apply-panel__btn" href={mailto}>
              메일 쓰기
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
